import { Link } from 'react-router-dom';
import { fetchDashboard } from '@/api/dashboard';
import { useAuthGate } from '@/hooks/useAuthGate';
import { AuthGate } from '@/components/AuthGate';
import { Icon } from '@/components/Icon';
import { KpiGrid } from '@/components/dashboard/KpiGrid';
import { TrendChart } from '@/components/dashboard/TrendChart';
import { GroupedBars } from '@/components/dashboard/GroupedBars';
import { HorizontalBarList, StatusBar } from '@/components/dashboard/Charts';
import { ActivityFeed } from '@/components/dashboard/ActivityFeed';
import { AttentionList } from '@/components/dashboard/AttentionList';

/**
 * Главная страница — сводка по портфелю менеджера. Все числа реальные, их
 * считает старая админка (GET /admin-api/v1/dashboard, см. DashboardController)
 * в том же скоупе, что и список клиентов: куратор видит только свои кабинеты.
 *
 * Доступ только по сессии BFF (та же SSO-cookie): нет сессии — страница-
 * заглушка со ссылкой на вход (см. AuthGate).
 */
export function DashboardPage() {
  const state = useAuthGate(fetchDashboard);

  return (
    <AuthGate state={state}>
      {(data) => (
        <section className="dashboard">
          <header className="dashboard__header">
            <div>
              <h1>{data.managerName ? `Добрый день, ${data.managerName}` : 'Дашборд'}</h1>
              <p className="dashboard__lede">
                {data.scope === 'all'
                  ? `Все кабинеты системы — сейчас их ${data.kpis.clients}.`
                  : `Клиенты, закреплённые за вами — сейчас их ${data.kpis.clients}.`}
              </p>
            </div>
            <Link className="btn btn--secondary" to="/clients">
              <Icon name="users" size={15} /> Все клиенты
            </Link>
          </header>

          <KpiGrid kpis={data.kpis} />

          <div className="dashboard__grid">
            <div className="card card--span2">
              <div className="card__head">
                <div>
                  <h2>Портфель по месяцам</h2>
                  <p className="card__hint">
                    Сколько кабинетов было на конец месяца, за последние {data.growth.length} мес.
                  </p>
                </div>
              </div>
              <TrendChart
                ariaLabel="Число клиентов по месяцам"
                points={data.growth.map((p) => ({
                  month: p.month,
                  value: p.clients,
                  delta: p.added,
                }))}
              />
            </div>

            <div className="card">
              <div className="card__head">
                <div>
                  <h2>Требуют внимания</h2>
                  <p className="card__hint">Сроки кабинетов и незаполненные подключения</p>
                </div>
              </div>
              <AttentionList items={data.attention} />
            </div>

            <div className="card">
              <div className="card__head">
                <div>
                  <h2>Оборот у.е.</h2>
                  <p className="card__hint">Выдано и списано по портфелю</p>
                </div>
              </div>
              <GroupedBars
                firstLabel="Выдано"
                secondLabel="Списано"
                points={data.ueFlow.map((p) => ({
                  month: p.month,
                  first: p.issued,
                  second: p.spent,
                }))}
              />
            </div>

            <div className="card">
              <div className="card__head">
                <div>
                  <h2>Статусы кабинетов</h2>
                  <p className="card__hint">Из чего состоит портфель</p>
                </div>
              </div>
              <StatusBar
                segments={[
                  { label: 'Активные', value: data.statusBreakdown.active, tone: 'good' },
                  { label: 'Демо', value: data.statusBreakdown.demo, tone: 'warn' },
                  { label: 'Отключённые', value: data.statusBreakdown.disabled, tone: 'neutral' },
                ]}
              />
            </div>

            <div className="card">
              <div className="card__head">
                <div>
                  <h2>Популярные тесты</h2>
                  <p className="card__hint">В скольких кабинетах подключён тест</p>
                </div>
              </div>
              <HorizontalBarList
                emptyText="В портфеле пока нет подключённых тестов."
                items={data.topTests.map((t) => ({
                  label: t.name ?? 'Без названия',
                  value: t.clientsCount,
                }))}
              />
            </div>

            {/* Во всю ширину: лента идёт последней и замыкает дашборд, а
                неполный ряд из одной узкой карточки оставлял пустое место. */}
            <div className="card card--span-all">
              <div className="card__head">
                <div>
                  <h2>Последние события</h2>
                  <p className="card__hint">Что происходило в кабинетах клиентов</p>
                </div>
                <Link className="card__link" to="/events">
                  Все события
                </Link>
              </div>
              <ActivityFeed items={data.recentActivity} />
            </div>
          </div>
        </section>
      )}
    </AuthGate>
  );
}
